/**
 * Pure Propose-step derivation kernels, extracted verbatim from the original `ProposeStep` body so the
 * `discovery-questions` primitive can call them by key instead of inlining the logic. Both are pure and
 * jest-safe (no worker/`@react-pdf` deps), so `./core` registers them statically.
 *
 *  - `deriveLdosFacts` — the Cisco Ready last-date-of-support sentence quoted next to the Lifecycle
 *    question (from the account's own install-base export), or null when there is nothing to quote.
 *  - `reconcileIqScenario` — the pure decision behind the effect that keeps the recorded Cisco IQ
 *    scenario consistent with what Profiling left open: what (if anything) to write, never the write.
 */
import type { InstallBaseUpload } from '../../api/types';

/** Turn the account's install-base lifecycle counts into the LDoS sentence, or null when nothing applies. */
export function deriveLdosFacts(installBase: InstallBaseUpload | null | undefined): string | null {
  const life = installBase?.lifecycle;
  if (!life?.counts) return null;
  const parts = [
    life.counts.pastSupport > 0
      ? `${life.counts.pastSupport} of ${life.totalSubCategories} sub-categories already hold equipment past its last date of support`
      : null,
    life.counts.endingSoon > 0 ? `${life.counts.endingSoon} more lose support within twelve months` : null,
    life.counts.noSecurityFixes > 0
      ? `${life.counts.noSecurityFixes} no longer receive security fixes`
      : null,
  ].filter(Boolean);
  if (parts.length === 0) return null;
  return `From their Cisco Ready export: ${parts.join(', ')}.`;
}

export interface IqReconcileInput {
  /** True when Profiling left exactly one possible scenario (the answer is settled, not asked). */
  iqSettled: boolean;
  /** The scenario ids still open given Profiling's connection state. */
  iqOptions: string[];
  /** The scenario currently recorded for this delivery ('' when none). */
  iqScenario: string;
}

/**
 * Decide how to reconcile the recorded IQ scenario with what Profiling left open. Returns `{ write }`
 * with the value to persist, or null when nothing needs to change. Pure: the caller performs the write.
 *
 *  - settled → write the sole option when it is not already recorded (Profiling answered it for us);
 *  - unsettled → clear a stale recorded scenario Profiling no longer supports.
 */
export function reconcileIqScenario({
  iqSettled,
  iqOptions,
  iqScenario,
}: IqReconcileInput): { write: string } | null {
  if (iqSettled) {
    return iqScenario !== iqOptions[0] ? { write: iqOptions[0] } : null;
  }
  if (iqScenario && !iqOptions.some((option) => option === iqScenario)) {
    return { write: '' };
  }
  return null;
}
